import { Box, Paper, Typography, Tabs, Divider } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { TabContext, TabPanel } from "@mui/lab";
import React, { useState } from "react";
import { CustomTab as Tab } from 'components/common/custom-tab';
import { useForm } from "react-hook-form";

import { InferenceForm } from "./types";
import { Form } from "./components/form";
import { Prompt } from "./components/tabs/prompt";
import { LoaderNotes } from "views/charge-settings/components/LoaderNotes";
import { Tools } from "./components/tabs/tools";

const useStyles = makeStyles((theme) => ({
  container: {
    overflow: 'hidden',
    gap: '15px',
    padding: theme.spacing(3),
    display: "flex",
  },
  textarea: {
    height: "150px",
    color: '#000000',
    padding: 5,
    border: "1.2px solid #c6c6c6",
    borderRadius: '5px',
    minWidth: "100%",
    maxWidth: "100%",
    fontFamily: "inherit",
    fontSize: '0.875rem',
  },
  panelcontainer: {
    padding: "10px",
    height: "100%"
  },
  text: {
    fontFamily: "inherit",
    wordWrap: "break-word",
    whiteSpace: "pre-wrap",
    color: "#000000"
  }
}));

export const InferenceView = () => {
  const classes = useStyles();
  const [currentTab, setCurrentTab] = useState<string>("prompt");
  const [thoughts, setThoughts] = useState<string>("");
  const [output, setOutput] = useState<string>("");
  const [isInferenceLoading, setIsInferenceLoading] = useState(false);

  const { control, handleSubmit } = useForm<InferenceForm>({
    defaultValues: {
      llmModel: "meta/llama-4-maverick",
      executor: "",
      systemMessage: "Answer as best you can. Always refer to the user as you. Do not include any UUID in your response.",
      userMessage: `You have access to the following tools:

{tools}

Use the following format:

Question: the input question you must answer
Thought: you should always think about what to do
Action: the action to take, should be one of [{tool_names}]
Action Input: the input to the action
Observation: the result of the action
... (this Thought/Action/Action Input/Observation can repeat N times)
Thought: I now know the final answer
Final Answer: the final answer to the original input question

Question: {input}
Thought: {agent_scratchpad}`
    }
  });

  return (
    <Box className={classes.container} style={{ height: "calc(100vh - 100px)" }}>
      <Form
        control={control}
        handleSubmit={handleSubmit}
        setCurrentTab={setCurrentTab}
        setOutput={setOutput}
        setThoughts={setThoughts}
        isInferenceLoading={isInferenceLoading}
        setIsInferenceLoading={setIsInferenceLoading}
      />
      <Paper style={{ flex: 2 }}>
        <Box style={{
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
        >
          <TabContext value={currentTab}>
            <Tabs
              value={currentTab}
              onChange={(e, value: string) => setCurrentTab(value)}
            >
              <Tab
                value="response"
                currentValue={currentTab}
                text="Response"
                disabled={output.length === 0}
              />
              <Tab
                value="thoughts"
                currentValue={currentTab}
                text="Thoughts"
                disabled={thoughts.length === 0}
              />
              <Tab
                value="prompt"
                currentValue={currentTab}
                text="Prompt"
              />
              <Tab
                value="tools"
                currentValue={currentTab}
                text="Tools"
              />
            </Tabs>
            <Box style={{ height: "100%", overflow: "scroll" }}>
              <TabPanel value="response" className={classes.panelcontainer}>
                {isInferenceLoading && (<LoaderNotes />)}
                <pre className={classes.text}>
                  {output}
                </pre>
              </TabPanel>
              <TabPanel value="thoughts" className={classes.panelcontainer}>
                {isInferenceLoading && thoughts.length === 0 && (<LoaderNotes />)}
                <pre className={classes.text}>
                  {thoughts}
                </pre>
              </TabPanel>
              <TabPanel value="prompt" className={classes.panelcontainer}>
                <Prompt control={control} />
              </TabPanel>
              <TabPanel value="tools" className={classes.panelcontainer}>
                <Tools />
              </TabPanel>
            </Box>
          </TabContext>
        </Box>
      </Paper>
    </Box>
  );
};
